import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const SPIN = 1.5; // seconds per turn
const TURNS = 3;
const FADE = 0.9; // the same fade-in applied to both the mark and the slogan
const EASE = [0.16, 1, 0.3, 1];
const WORD = 0.34; // seconds between words as the slogan types itself out
const HOLD = 5; // the finished slogan sits on screen for a full five seconds

const WORDS = [
  { text: "Our", accent: false },
  { text: "Youth.", accent: false },
  { text: "Our", accent: true },
  { text: "Plan.", accent: true },
  { text: "Our", accent: false },
  { text: "Football.", accent: false },
];

/** Word by word, like a typewriter, with the caret blinking until the last word lands. */
const TypedSlogan = ({ onTyped }) => {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (shown >= WORDS.length) {
      onTyped();
      return;
    }
    const t = setTimeout(() => setShown((n) => n + 1), WORD * 1000);
    return () => clearTimeout(t);
  }, [shown, onTyped]);

  return (
    <p className="font-display text-2xl font-bold uppercase leading-[1.05] tracking-tight text-white md:text-4xl lg:text-5xl"
      data-testid="intro-slogan-typed">
      {WORDS.slice(0, shown).map((w, i) => (
        <motion.span
          key={`${w.text}-${i}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className={w.accent ? "text-maasai" : undefined}
        >
          {w.text}{" "}
        </motion.span>
      ))}
      <motion.span
        aria-hidden="true"
        className="inline-block w-[0.08em] self-stretch bg-ochre align-middle"
        style={{ height: "0.9em" }}
        animate={{ opacity: shown >= WORDS.length ? [1, 0] : [1, 1, 0, 0] }}
        transition={{ duration: shown >= WORDS.length ? 1.1 : 0.7, repeat: Infinity, ease: "linear" }}
      />
    </p>
  );
};

/** The landing moment: the mark turns three times, the slogan joins it, then the home page arrives. */
export const Intro = ({ onDone }) => {
  const still = useReducedMotion();
  const [slogan, setSlogan] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (still) {
      onDone();
      return;
    }
    const a = setTimeout(() => setSlogan(true), SPIN * TURNS * 1000);
    return () => clearTimeout(a);
  }, [still, onDone]);

  const typed = useCallback(() => {
    const t = setTimeout(() => setGone(true), HOLD * 1000);
    return () => clearTimeout(t);
  }, []);

  const skip = () => setGone(true);

  return (
    <AnimatePresence onExitComplete={onDone}>
      {!gone ? (
        <motion.div
          key="intro"
          onClick={skip}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[200] flex cursor-pointer flex-col items-center justify-center gap-8 bg-[#050505] px-6 md:flex-row md:gap-12"
          data-testid="intro-overlay"
        >
          <motion.div
            className="relative h-[168px] w-[168px] shrink-0 md:h-[220px] md:w-[220px]"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: FADE, ease: EASE }}
          >
            <motion.div
              className="absolute inset-0 rounded-full border border-white/15"
              animate={{ rotate: 360 * TURNS }}
              transition={{ duration: SPIN * TURNS, ease: "linear" }}
            >
              <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-maasai" />
              <span className="absolute bottom-0 left-1/2 h-1.5 w-1.5 -translate-x-1/2 translate-y-1/2 rounded-full bg-ochre" />
            </motion.div>
            <motion.div
              className="absolute inset-[9%] rounded-full border border-dashed border-white/10"
              animate={{ rotate: -360 * TURNS }}
              transition={{ duration: SPIN * TURNS, ease: "linear" }}
            />
            <motion.img
              src="/brand/kskf-short-white.png"
              alt="KickStartKenyaFootball"
              className="absolute left-1/2 top-1/2 w-[62%] -translate-x-1/2 -translate-y-1/2 object-contain"
              animate={{ rotate: 360 * TURNS }}
              transition={{ duration: SPIN * TURNS, ease: [0.42, 0, 0.58, 1] }}
              data-testid="intro-mark"
            />
          </motion.div>

          <AnimatePresence>
            {slogan ? (
              <motion.div
                initial={{ opacity: 0, x: 18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: FADE, ease: EASE }}
                className="text-center md:text-left"
                data-testid="intro-slogan"
              >
                <TypedSlogan onTyped={typed} />
                <p className="mono mt-4 text-[0.58rem] uppercase tracking-[0.24em] text-zinc-500">
                  KickStartKenyaFootball
                </p>
              </motion.div>
            ) : null}
          </AnimatePresence>

        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
